import {FormState, FormStateWithIssues} from '@raison/form-models';
import {useLocationState} from '@raison/navigation';
import {ErroneousServerResult} from '@raison/network-facade';
import {RootLayout, RsnAmount, RsnAmountInput, RsnButton, RsnDialog, RsnPicker} from '@raison/ui-kit';
import * as Schema from 'effect/Schema';
import React, {useReducer} from 'react';
import {useNavigate} from 'react-router';
import {createCurrencyFormatter, useCurrencyFormat} from '~/entities/currencies';
import {OperationDetail, OperationHeader} from '~/entities/operations-fiat';
import {PaymentSystemFeeDialog} from '~/processes/fiat-operations/ui/Shared/PaymentSystemFeeDialog';
import {WithdrawSwiftRequisitesPrefetcher} from '~/processes/fiat-operations/ui/Withdraw/WithdrawSwiftRequisitesPrefetcher';
import {OperableAccount, OperableBank} from '~/redux/finances/domain';
import {useValidateAmount} from '../../api/withdraw/validateAmount';
import {useWithdrawFee} from '../../api/withdraw/withdrawFee';
import {transformFlagsToHint} from '../../model/transformFlagsToHint';
import {AddressDialog} from '../AddressDialog';
import {IbanDialog} from '../IbanDialog';
import {LabelWithInfo} from '../LabelWithInfo';
import {OperationInfoSkeleton} from '../OperationInfoSkeleton';
import {WithdrawSepaRequisitesPrefetcher} from './WithdrawSepaRequisitesPrefetcher';

type OperationIntroProps = {
    providerName: string;
    accounts: OperableAccount[];
    methods: OperableBank[];
    account: OperableAccount;
    method: OperableBank | undefined;
    onOperationChange: (account: OperableAccount, method: OperableBank | null) => void;
    onStepChange: (amount: RsnAmount) => void;
    onProblem: (problem: ErroneousServerResult) => void;
    initialAmount: RsnAmount;
};

type WithdrawFormState = ReturnType<typeof WithdrawFormState>;
const WithdrawFormState = (initial: {amount: RsnAmount} | null) =>
    FormState.from({
        amount: initial?.amount || RsnAmount.empty(),
    });

type Values = FormState.inferValues<WithdrawFormState>;

export function WithdrawIntro({
    providerName,
    accounts,
    methods,
    account,
    method,
    onOperationChange,
    onStepChange,
    onProblem,
    initialAmount,
}: OperationIntroProps) {
    const navigate = useNavigate();

    const {format: formatAsSubject} = useCurrencyFormat(account);

    const [form, dispatchForm] = useReducer(
        FormState.reduce<Values>,
        {
            amount: initialAmount,
        },
        WithdrawFormState
    );

    const accountsOptions: RsnPicker.OptionObject<OperableAccount>[] = accounts.map(account => {
        const {format: formatAsPickerOptionBalance} = createCurrencyFormatter({
            code: account.currencyCode,
            decimals: account.currencyDecimals,
        });
        return {
            value: account,
            icon: {src: account.icon},
            title: account.currencyName,
            hint: `Balance: ${formatAsPickerOptionBalance(account.balance)} `,
        };
    });

    const methodsOptions: RsnPicker.OptionObject<OperableBank>[] = methods.map(method => ({
        value: method,
        icon: {src: method.icon},
        title: method.name,
        ...transformFlagsToHint(method.flags, method.description),
    }));

    const handleAccountChange = (account: OperableAccount) => onOperationChange(account, method || null);
    const handleMethodChange = (method: OperableBank) => onOperationChange(account, method);

    const {
        data: {value: fee} = {},
        isLoading: isFeesLoading,
        isDebouncing: isFeesDebouncing,
    } = useWithdrawFee({
        accountId: account.id,
        methodId: method?.id,
        amount: RsnAmount.toString(form.fields.amount.value) || '0',
    });

    const t = {
        accountLabel: 'From',
        methodLabel: 'Using',
        headerSubtitle: `${providerName} provider`,
        minHint: isFeesLoading
            ? 'Loading...'
            : `Min: ${fee?.minimalAmountExclusive ? 'more than ' : ''}${formatAsSubject(fee?.minimalAmount ?? 0)}`,
        notEnoughFunds: 'Not enough funds',
        feeLabel: 'Fee',
        fee: formatAsSubject(fee?.fee ?? 0),
        totalLabel: (() => {
            if (fee == null) {
                return 'Total';
            }
            return fee.commissionAbove ? 'Total spend' : 'Total receive';
        })(),
        total: formatAsSubject(fee?.total ?? 0),
    };

    const formWithIssues = FormStateWithIssues.fromRules(form, {
        amount: [
            fee?.minimalAmountExclusive
                ? RsnAmount.validateMinExclusive(fee.minimalAmount, t.minHint)
                : RsnAmount.validateMin(fee?.minimalAmount || 0, t.minHint),
        ],
    });

    const {mutateAsync: validate} = useValidateAmount(account.id);

    const {fields, isSubmitDisabled, onSubmit} = FormState.selectFormView(formWithIssues, {
        dispatch: dispatchForm,
        async submit() {
            if (method?.flags?.some(f => f.reasonCode === 'ADDRESS_UNVERIFIED')) {
                setDialog('address');
                return;
            }
            if (method?.flags?.some(f => f.reasonCode === 'IBAN_PENDING')) {
                setDialog('iban');
                return;
            }
            const amount = RsnAmount.toString(fields.amount.value) || '0';
            const {issues} = await validate(amount);
            if (issues) {
                if (issues.message.startsWith('Not enough money')) {
                    return {amount: t.notEnoughFunds};
                }
                onProblem(issues);
                return false;
            }
            onStepChange(fields.amount.value);
            return true;
        },
    });

    const [dialog, setDialog] = useLocationState('withdraw-intro-dialog', Schema.Literal('fee', 'iban', 'address'));
    const closeDialog = () => navigate(-1);

    const canSubmit = !isFeesLoading && !isFeesDebouncing && !isSubmitDisabled && !!method;

    return (
        <RootLayout title={'Withdraw'} pageHeader={<OperationHeader subtitle={t.headerSubtitle} />}>
            <form
                className={'flex flex-1 flex-col gap-6 p-4'}
                onSubmit={e => {
                    e.preventDefault();
                    onSubmit(e);
                }}
            >
                <RsnPicker
                    title={'From account'}
                    label={t.accountLabel}
                    onChange={handleAccountChange}
                    options={accountsOptions}
                    value={account}
                    keyedBy={'id'}
                    disabled={accounts.length <= 1}
                />
                <RsnPicker
                    title={'Withdraw method'}
                    label={t.methodLabel}
                    onChange={handleMethodChange}
                    options={methodsOptions}
                    value={method}
                    keyedBy={'id'}
                    disabled={methods.length <= 1}
                />
                <div className={'mt-auto'}>
                    <RsnAmountInput
                        {...fields.amount}
                        helperText={t.minHint}
                        maxDecimals={account.currencyDecimals}
                        maxIntegers={10}
                        suffix={account.currencyCode}
                        hintAction={<RsnAmountInput.HintActionUseAll all={account.balance} />}
                    />
                </div>
                <div className={'flex flex-col gap-4'}>
                    <OperationDetail.Layout>
                        {isFeesLoading ? (
                            <OperationInfoSkeleton />
                        ) : (
                            <>
                                <OperationDetail
                                    label={
                                        fee?.showFeeInfo ? (
                                            <LabelWithInfo onClick={() => setDialog('fee')}>{t.feeLabel}</LabelWithInfo>
                                        ) : (
                                            t.feeLabel
                                        )
                                    }
                                >
                                    {t.fee}
                                </OperationDetail>
                                <OperationDetail label={t.totalLabel} variant={'total'}>
                                    {t.total}
                                </OperationDetail>
                            </>
                        )}
                    </OperationDetail.Layout>
                    <RsnButton.Primary disabled={!canSubmit} type={'submit'}>
                        Next step
                    </RsnButton.Primary>
                </div>
            </form>

            {method?.method === 'sepa' && <WithdrawSepaRequisitesPrefetcher account={account} />}
            {method?.method === 'swift' && <WithdrawSwiftRequisitesPrefetcher account={account} />}

            <RsnDialog.Controller onClose={closeDialog}>
                {(() =>
                    (dialog === 'address' && <AddressDialog />) ||
                    (dialog === 'iban' && <IbanDialog />) ||
                    (dialog === 'fee' && (
                        <PaymentSystemFeeDialog
                            feeInfo={fee?.feeInfo}
                            paymentNetwork={method?.method}
                            operationType={'withdraw'}
                        />
                    )))()}
            </RsnDialog.Controller>
        </RootLayout>
    );
}
